import { Component, DestroyRef, effect, inject } from '@angular/core';
import { takeUntilDestroyed, toSignal } from "@angular/core/rxjs-interop";
import { NavigationStart, Router } from '@angular/router';

import {
    BcEmployeeAuthFacade,
    BcEmployeeInitAuthAction,
    BcEmployeeLoginSuccessfulAction,
    BcEmployeeLogoutAction,
    BcPrivilegesFacade
} from '@bayclubs/employee-auth';
import { ApplicationGroupsService, AuthService, ClubsService } from "@bayclubs/horizon";

import { OfficeSecurityService } from '@core/core.module';
import { environment } from '@environments/environment';
import { NavigationFacade } from '@navigation/navigation.module';

import { Actions, ofActionSuccessful } from '@ngxs/store';
import { filter, Subscription } from 'rxjs';
import { distinctUntilChanged, map } from "rxjs/operators";

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    standalone: false
})
export class AppComponent {

    private readonly isMsal = environment.isMsalAuthenticationEnabled;
    private readonly horizonAuth = this.isMsal ? inject(AuthService) : null;
    showMenubar = toSignal(this.isMsal ? this.privilegesService.canUseCourtBooking : this.authFacade.isAuthorized$, {
        initialValue: this.isMsal
            ? this.privilegesService.canUseCourtBookingSnapshot
            : this.authFacade.getIsAuthorizedSnapshot()
    });
    subscription: Subscription;
    constructor(
        private actions$: Actions,
        private authFacade: BcEmployeeAuthFacade,
        private privilegesService: OfficeSecurityService,
        private navigationFacade: NavigationFacade,
        private router: Router,
        private destroyRef: DestroyRef,
        private applicationGroupsService: ApplicationGroupsService,
        private clubsService: ClubsService,
        private privilegesFacade: BcPrivilegesFacade
    ) {
        this.subscription = new Subscription();
        if (this.isMsal) {
            this.initMsalSubscriptions();
        } else {
            this.initializeActionsSubscriptions();
            this.initAuth();
        }
        this.redirectHashLocationLinksToPathLocation();
        this.initHorizonProviders();
    }

    private initAuth(): void {
        this.authFacade.initAuth();
    }

    private initMsalSubscriptions(): void {
        effect(() => {
            if (this.horizonAuth!.isAuthenticated()) {
                // refreshPrivileges dispatches BcRefreshPrivilegesSuccessAction (no Navigate side effect).
                // loadPrivileges would dispatch BcEmployeeLoginSuccessfulAction -> Navigate(['/']),
                // redirecting the user away from the route they deep-linked to.
                this.privilegesFacade.refreshPrivileges();
                this.navigationFacade.loadAuthenticatedMenu();
            }
        });

        this.subscription.add(
            this.privilegesService.privilegesChanged$.subscribe(() => {
                this.navigationFacade.refreshMenu();
            })
        );
    }

    initializeActionsSubscriptions() {
        this.subscription.add(
            this.actions$.pipe(ofActionSuccessful(BcEmployeeLoginSuccessfulAction)).subscribe(() => {
                this.loadAuthenticatedData();
            })
        );
        this.subscription.add(
            this.actions$.pipe(ofActionSuccessful(BcEmployeeInitAuthAction)).subscribe(() => {
                this.loadAuthenticatedData();
            })
        );
        this.subscription.add(
            this.actions$.pipe(ofActionSuccessful(BcEmployeeLogoutAction)).subscribe(() => {
                this.navigationFacade.clearMenu();
            })
        );

        this.subscription.add(
            this.privilegesService.privilegesChanged$.subscribe(value => {
                this.navigationFacade.refreshMenu();
            })
        );
    }

    loadAuthenticatedData() {
        const isAuthenticated = this.authFacade.getIsAuthenticatedSnapshot();
        if (isAuthenticated) {
            this.navigationFacade.loadAuthenticatedMenu();
        }
    }


    private redirectHashLocationLinksToPathLocation() {
        this.router.events
            .pipe(filter((event) => event instanceof NavigationStart && /^\/#/.test(event.url)))
            .subscribe(
                (event) => {
                    if (event instanceof NavigationStart) {
                        console.debug('Found path created with HashLocationStrategy. Redirecting to PathLocationStrategy');
                        this.router.navigateByUrl(event.url.replace('/#', ''))
                    }
                }
            );
    }

    private initHorizonProviders(): void {
        if (this.isMsal) {
            effect(() => {
                if (this.horizonAuth!.isAuthenticated()) {
                    this.applicationGroupsService.init();
                    this.clubsService.init();
                }
            });
        } else {
            this.authFacade.token$.pipe(
                map(token => token?.access_token ?? null),
                distinctUntilChanged(),
                filter(Boolean),
                takeUntilDestroyed(this.destroyRef)
            ).subscribe(() => {
                this.applicationGroupsService.init();
                this.clubsService.init();
            });
        }
    }
}
